import _ from "lodash";
import { Store } from "../../utils/Store";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import MarketplaceClient from "../../clients/MarketplaceClient";
import MerchantEvaluationsActions from "../../actions/MerchantEvaluationsActions";
import * as TrackingUtil from "../../utils/TrackingUtil";
import { MerchantLoadingState } from "./MerchantInformationStore";
import { MerchantEvaluations, MerchantEvaluationsResponse } from "../../datatypes/merchant/MerchantEvaluations";

type State = {
    merchantEvaluations: {
        [shopId: string]: MerchantEvaluations[];
    };
    evaluationsOffsets: {
        [shopId: string]: number;
    };
    merchantTotalEvaluations: {
        [shopId: string]: number;
    };
    status: MerchantLoadingState | null | undefined;
};

let state: State;

class MerchantEvaluationsStoreImpl extends Store {
    constructor() {
        super();
        state = this.getEmptyState();
        this.listenTo(MerchantEvaluationsActions.getMerchantEvaluations, this.loadMoreMerchantEvaluations);
    }

    getEmptyState(): State {
        return {
            merchantEvaluations: {},
            evaluationsOffsets: {},
            merchantTotalEvaluations: {},
            status: null
        };
    }

    getLoadedMerchantEvaluations(shopId: string): MerchantEvaluations[] {
        return state.merchantEvaluations[shopId];
    }

    getTotalEvaluationsCount(shopId: string): number {
        return state.merchantTotalEvaluations[shopId];
    }

    getCurrentMerchantEvaluationsOffset(shopId: string): number {
        return state.evaluationsOffsets[shopId];
    }

    // Makes a request to the marketplace service and loads the next 10 evaluations starting from the offset
    loadMoreMerchantEvaluations(shopId: string | null | undefined) {
        const shopIdCopy: string | null | undefined = shopId;
        if (!shopIdCopy) {
            return;
        }

        if (!(shopIdCopy in state.evaluationsOffsets)) {
            state.evaluationsOffsets[shopIdCopy] = 0;
        }

        const offset = state.evaluationsOffsets[shopIdCopy];
        state.status = "loading";
        this.trigger();
        MarketplaceClient.getMerchantEvaluations(
            SessionStore.getCountry(),
            SessionStore.getLocale(),
            shopIdCopy,
            offset
        )
            .then((response: MerchantEvaluationsResponse) => this.onSuccess(offset, shopIdCopy, response))
            .catch(e => this.onFailed(e));
    }

    onSuccess(offset: number, shopId: string, response: MerchantEvaluationsResponse) {
        state.status = "loaded";

        // Avoiding race condition for multiple markeplace client calls with same offset
        if ((state.merchantEvaluations[shopId] || []).length <= offset) {
            state.merchantEvaluations[shopId] =
                shopId in state.merchantEvaluations
                    ? _.concat(state.merchantEvaluations[shopId], response.evaluations)
                    : response.evaluations;
            state.evaluationsOffsets[shopId] = offset + response.evaluations.length;
            state.merchantTotalEvaluations[shopId] = response.total_count;
            this.trigger();
        }
    }

    onFailed(error: string) {
        TrackingUtil.trackUIError("MerchantEvaluationsStore", "Fetching of merchant evaluations failed", error);
        state.status = "failed";
        this.trigger();
    }

    getLoadingState() {
        return state.status;
    }

    // For testing
    resetState() {
        state = this.getEmptyState();
    }
}

export const MerchantEvaluationsStore = new MerchantEvaluationsStoreImpl();
